import type { SignalReading } from '@bloo-team/core-shell'
import type { CmCase, CmCategory, Workbook } from '@bloo-team/api-client'
import type { CoverageTotals } from '../lib/coverage'

/**
 * What Vantage's journey signals DECIDE, with the reads already done
 * (`docs/home-journey-requirements.md` §5, S4). Pure — data in, a
 * `SignalReading` out — so every threshold is testable without a hook.
 *
 * The phases these answer are Detect ("does the data produce security signal,
 * and is that signal tuned") and the security half of Operate ("is it run as
 * a programme"). A reading's `value` is the positive fact, never the defect
 * count: the row answers "is this done".
 */

const n = (v: number) => v.toLocaleString('en-US')

/* --------------------------------------------------------- phase 3: detect */

/**
 * A library that exists. Ten is the bar the requirements set, and it is a
 * judgement rather than a measurement (§12) — a deployment running three
 * detections is not detecting, whatever those three are.
 */
export const DETECTIONS_BAR = 10

/** Silence past this is a detection that has stopped earning its place. */
export const SILENT_DAYS = 14

export function detectionsEnabled(workbooks: Workbook[]): SignalReading {
  const enabled = workbooks.filter((w) => !w.disabled).length
  const state = enabled >= DETECTIONS_BAR ? 'healthy' : enabled > 0 ? 'partial' : 'missing'
  return {
    state,
    value: `${n(enabled)} of ${n(workbooks.length)} enabled`,
    detail:
      enabled >= DETECTIONS_BAR
        ? 'A library is running against the data.'
        : enabled > 0
          ? `Fewer than ${DETECTIONS_BAR} detections are enabled — too few to call this covered.`
          : 'Nothing is watching the data: no detection is enabled, so no signal can be raised.',
    action: { label: enabled >= DETECTIONS_BAR ? 'Library' : 'Enable', to: '/vantage/detections', search: { status: 'disabled' } },
  }
}

/**
 * Detections that fire. A workbook enabled but silent for a fortnight is
 * either broken or pointed at data that stopped — the same reading the
 * Detections page's `silent` lens takes, from the same two fields.
 */
export function detectionsFiring(workbooks: Workbook[], now: number): SignalReading {
  const enabled = workbooks.filter((w) => !w.disabled)
  if (enabled.length === 0) return { state: 'na', value: null }
  const cutoff = now - SILENT_DAYS * 24 * 3600_000
  const silent = enabled.filter((w) => w.signal_count === 0 && (w.signal_last_seen === null || w.signal_last_seen < cutoff))
  const firing = enabled.length - silent.length
  return {
    state: silent.length === 0 ? 'healthy' : 'partial',
    value: `${n(firing)} of ${n(enabled.length)} firing`,
    detail:
      silent.length === 0
        ? 'Every enabled detection has fired recently.'
        : `${n(silent.length)} enabled but silent for ${SILENT_DAYS} days — broken, or pointed at data that stopped.`,
    ...(silent.length > 0
      ? { action: { label: 'Silent', to: '/vantage/detections', search: { silent: 'true' } } }
      : {}),
  }
}

/** Coverage of the ATT&CK matrix, as the Coverage page counts it. */
export const COVERAGE_BAR = 0.15

export function attackCoverage(totals: CoverageTotals | undefined): SignalReading {
  const covered = totals?.covered_techniques ?? 0
  const total = totals?.total_techniques ?? 0
  if (total === 0) return { state: 'loading', value: null }
  const share = covered / total
  return {
    state: share >= COVERAGE_BAR ? 'healthy' : covered > 0 ? 'partial' : 'missing',
    value: `${n(covered)} of ${n(total)} techniques`,
    detail:
      share >= COVERAGE_BAR
        ? `${Math.round(share * 100)}% of the matrix has a detection behind it.`
        : `${Math.round(share * 100)}% of the matrix is covered — an attack has a great deal of room to move unseen.`,
    action: { label: 'Coverage', to: '/vantage/coverage' },
  }
}

/** Signal is actually arriving from those detections. */
export function signalsFlowing(count: number): SignalReading {
  return {
    state: count > 0 ? 'healthy' : 'missing',
    value: count > 0 ? `${n(count)} in 24h` : 'none in 24h',
    detail:
      count > 0
        ? 'Detections are raising signal an analyst can work.'
        : 'No signal in a day. Either nothing happened, or nothing is reaching the detections — and the two look identical from here.',
    action: { label: 'Signals', to: '/vantage/signals' },
  }
}

/**
 * Tuning. A library nobody has suppressed anything in is either brand new or
 * drowning its analysts, and both are worth saying out loud.
 */
export function tuningInUse(rules: { is_enable: boolean }[]): SignalReading {
  const active = rules.filter((r) => r.is_enable).length
  return {
    state: active > 0 ? 'healthy' : 'missing',
    value: active > 0 ? `${n(active)} suppression rule${active === 1 ? '' : 's'}` : 'none',
    detail:
      active > 0
        ? 'Known-benign signal is being suppressed rather than triaged again each time.'
        : 'Nothing is suppressed. Every false positive costs an analyst the same minute it cost last week.',
    action: { label: 'Tuning', to: '/vantage/detections', search: { tab: 'suppression' } },
  }
}

/* -------------------------------------------------------- phase 4: operate */


/** Cases are worked to a conclusion, not just opened — over the cases opened in the last 90 days. */
export function casesWorked(counts: Record<CmCategory, number> | undefined): SignalReading {
  const total = counts ? counts.new + counts.in_progress + counts.on_hold + counts.closed : 0
  if (total === 0) {
    return {
      state: 'missing',
      value: 'none',
      detail: 'No case was opened in the last 90 days. Signal that never becomes a case is signal nobody owned.',
      action: { label: 'Cases', to: '/vantage/cases' },
    }
  }
  const closed = counts!.closed
  return {
    state: closed > 0 ? 'healthy' : 'partial',
    value: `${n(closed)} of ${n(total)} closed`,
    detail:
      closed > 0
        ? 'Cases reach a conclusion rather than accumulating.'
        : 'Every case opened is still open. A queue that only grows is not a process.',
    action: { label: 'Cases', to: '/vantage/cases' },
  }
}

/**
 * Response time is KNOWN. Deliberately not "good": the console has no target
 * to compare against, and inventing one would be worse than saying nothing
 * (§12 open question 2).
 */
export function caseResponseKnown(metrics: { mtta: number | null; mttr: number | null } | undefined): SignalReading {
  const mtta = metrics?.mtta ?? null
  const mttr = metrics?.mttr ?? null
  if (mtta === null && mttr === null) {
    return {
      state: 'missing',
      value: 'not measured',
      detail: 'Neither time-to-acknowledge nor time-to-resolve is known, so response cannot be held to anything.',
      action: { label: 'Cases', to: '/vantage/cases' },
    }
  }
  const hrs = (s: number | null) => (s === null ? '—' : s >= 3600 ? `${(s / 3600).toFixed(1)}h` : `${Math.round(s / 60)}m`)
  return {
    state: mtta !== null && mttr !== null ? 'healthy' : 'partial',
    value: `ack ${hrs(mtta)} · resolve ${hrs(mttr)}`,
    detail: 'Measured, not judged: the console holds no target to compare these against.',
    action: { label: 'Cases', to: '/vantage/cases' },
  }
}

/** Open cases have somebody's name on them. */
export function caseOwnership(items: Pick<CmCase, 'category' | 'assignee'>[]): SignalReading {
  const open = items.filter((c) => c.category !== 'closed')
  if (open.length === 0) return { state: 'na', value: null }
  const unowned = open.filter((c) => !c.assignee).length
  return {
    state: unowned === 0 ? 'healthy' : 'partial',
    value: `${n(open.length - unowned)} of ${n(open.length)} owned`,
    detail:
      unowned === 0
        ? 'Every open case has a handler.'
        : `${n(unowned)} open case${unowned === 1 ? '' : 's'} with nobody's name on it.`,
    action: { label: 'Cases', to: '/vantage/cases' },
  }
}

/** Somebody goes looking, rather than only answering alarms. */
export function huntingCadence(hunts: unknown[]): SignalReading {
  return {
    state: hunts.length > 0 ? 'healthy' : 'missing',
    value: hunts.length > 0 ? `${n(hunts.length)} saved` : 'none',
    detail:
      hunts.length > 0
        ? 'Somebody is looking for what the detections do not name.'
        : 'No hunt has been saved. A programme that only answers alarms only finds what it already knows to look for.',
    action: { label: 'Hunts', to: '/vantage/hunts' },
  }
}
